import React, { useState } from "react";
import { API_BASE_URL } from "../config";
import { Star, Send } from "lucide-react";

const StarRatingInput = ({ rating, onChange, hovered, onHover }) => {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((value) => (
        <button
          key={value}
          type="button"
          onClick={() => onChange(value)}
          onMouseEnter={() => onHover(value)}
          onMouseLeave={() => onHover(0)}
          className="p-1 transition-transform hover:scale-110"
        >
          <Star
            size={28}
            className={`transition-colors ${value <= (hovered || rating)
              ? "text-yellow-400 fill-current"
              : "text-gray-300"
              }`}
          />
        </button>
      ))}
    </div>
  );
};

const ReviewForm = ({ productId, onReviewSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (rating === 0) {
      setError("กรุณาให้คะแนน");
      return;
    }

    if (!comment.trim()) {
      setError("กรุณาเขียนความคิดเห็น");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/reviews/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          productId: parseInt(productId),
          rating,
          comment: comment.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to submit review");
      }

      setRating(0);
      setComment("");
      if (onReviewSubmitted) onReviewSubmitted();
    } catch (error) {
      console.error("Error submitting review:", error);
      setError(error.message || "ไม่สามารถส่งรีวิวได้ กรุณาลองใหม่");
    } finally {
      setSubmitting(false);
    }
  };

  const ratingLabels = ["", "แย่มาก", "แย่", "พอใช้", "ดี", "ดีมาก"];

  return (
    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">
        เขียนรีวิวสินค้า
      </h3>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Rating */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            คะแนน
          </label>
          <div className="flex items-center gap-4">
            <StarRatingInput
              rating={rating}
              onChange={setRating}
              hovered={hoveredRating}
              onHover={setHoveredRating}
            />
            {(hoveredRating || rating) > 0 && (
              <span className="text-sm text-gray-600 font-medium">
                {ratingLabels[hoveredRating || rating]}
              </span>
            )}
          </div>
        </div>

        {/* Comment */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            ความคิดเห็น
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="บอกเล่าประสบการณ์การใช้งานสินค้า..."
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none resize-none transition-all"
          />
        </div>

        {/* Error */}
        {error && (
          <p className="text-red-600 text-sm">{error}</p>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Send size={18} />
              ส่งรีวิว
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default ReviewForm;
